import React, { useState, useEffect, useCallback } from "react";
import apiClient from "api/axiosConfig";
import TemplateTablePresentational from "../TemplatesList/TemplateTableView";
import { formatDate } from "utlis/formatDate";
import { getTemplateType, parseLanguages } from "./utils/templateHelpers";
import { buildQueryParams } from "./utils/queryBuilder";
import Spinner from "components/Common/Spinner";

const TemplatesListPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [perPage, setPerPage] = useState();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [showLoadingModal, setShowLoadingModal] = useState(false);

  const fetchTemplates = useCallback(async (page, query) => {
    setLoading(true);
    setError(null);
    try {
      const queryString = buildQueryParams(page, query);
      const response = await apiClient.get(`/templates?${queryString}`);
      const { data: fetchedTemplates, meta } = response.data;
      const formattedTemplates = fetchedTemplates.map((template) => ({
        ...template,
        type: getTemplateType(template.type),
        languages: parseLanguages(template.target_languages),
      }));
      setTemplates(formattedTemplates);
      setCurrentPage(meta.current_page);
      setTotalPages(meta.last_page);
      setPerPage(meta.per_page);
    } catch (e) {
      console.error("Error fetching templates:", e);
      setError("Failed to fetch templates. Please try again later.");
      setTemplates([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchTemplates(currentPage, searchQuery);
  }, [currentPage, searchQuery, fetchTemplates]);

  const handleSearchInputChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleDeleteClick = (id) => {
    setDeletingId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingId) return;
    setShowDeleteModal(false);
    setShowLoadingModal(true);
    try {
      await apiClient.delete(`/templates/${deletingId}`);
      if (templates.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        await fetchTemplates(currentPage, searchQuery);
      }
    } catch (e) {
      console.error("Error deleting template:", e);
      setError("Failed to delete template. Please try again later.");
    } finally {
      setShowLoadingModal(false);
      setDeletingId(null);
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeletingId(null);
  };

  const handleLanguageAddSuccess = () => {
    fetchTemplates(currentPage, searchQuery);
  };

  return (
    <>
      <TemplateTablePresentational
        templates={templates}
        loading={loading}
        error={error}
        searchQuery={searchQuery}
        handleSearchInputChange={handleSearchInputChange}
        handlePageChange={handlePageChange}
        handleDelete={handleDeleteClick}
        currentPage={currentPage}
        totalPages={totalPages}
        formatDate={formatDate}
        perPage={perPage}
        deletingId={deletingId}
        onLanguageAdded={handleLanguageAddSuccess}
      />

      {showDeleteModal && (
        <>
          <div className="modal fade show" style={{ display: "block" }} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Are you sure want to delete ?</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={handleCloseDeleteModal}
                  />
                </div>
                <div className="modal-body">
                  <p className="text-left">
                    This will delete base and translated templates from phishing manager
                    and synced templates from GoPhish too. Are you sure want to delete?
                  </p>
                </div>
                <div className="modal-footer justify-content-center">
                  <button className="btn btn-danger me-2" onClick={confirmDelete}>
                    Yes
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={handleCloseDeleteModal}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
      <Spinner text="Loading Templates..." isActive={loading} />
      <Spinner text="Deleting Templates..." isActive={showLoadingModal} />
    </>
  );
};

export default TemplatesListPage;